/**
 * PLY: a header that describes its own layout, then the data in that layout.
 *
 * The header is always text and says which elements the file holds, how many of each, and
 * the type of every property. Reading it is most of the work; the body is then a matter of
 * walking a known number of records of a known size.
 *
 * Three encodings exist and all three are in use: ASCII, and binary in either byte order.
 * The binary ones are why the property types have to be read properly rather than assumed —
 * a vertex may be three doubles, or three floats followed by three colour bytes, and
 * guessing wrong reads the file at the wrong stride and produces bounds made of noise.
 */

import { BoundsAccumulator } from './bounds.js';
import type { FileColourFormat, ModelEncoding, ModelStats } from './types.js';

export type PlyProblem =
  'no-header' | 'bad-header' | 'no-vertices' | 'truncated' | 'non-finite-vertex';

export type PlyReading =
  | { readonly ok: true; readonly stats: ModelStats }
  | { readonly ok: false; readonly problem: PlyProblem; readonly message: string };

/** Bytes each scalar type occupies, under every spelling the format allows. */
const SCALAR_BYTES = new Map<string, number>([
  ['char', 1],
  ['int8', 1],
  ['uchar', 1],
  ['uint8', 1],
  ['short', 2],
  ['int16', 2],
  ['ushort', 2],
  ['uint16', 2],
  ['int', 4],
  ['int32', 4],
  ['uint', 4],
  ['uint32', 4],
  ['float', 4],
  ['float32', 4],
  ['double', 8],
  ['float64', 8],
]);

interface Property {
  readonly name: string;
  readonly type: string;
  /** Present when the property is a list: its length is written first, in this type. */
  readonly countType?: string;
}

interface Element {
  readonly name: string;
  readonly count: number;
  readonly properties: Property[];
}

interface Header {
  readonly encoding: ModelEncoding;
  readonly littleEndian: boolean;
  readonly elements: Element[];
  /** Where the body starts, in bytes. */
  readonly dataOffset: number;
}

/** Long enough for any plausible header; a PLY header is metadata, not data. */
const MAX_HEADER_BYTES = 64 * 1024;

/**
 * What a colour channel may be called, in each of the three channels.
 *
 * The list is three.js's, deliberately: the renderer's `PLYLoader` is what actually builds
 * the colours, so recognising a set of names it would ignore would produce a model
 * declared to have its own colours and rendered without any — and with the colour picker
 * withheld, no way to give it one.
 */
const COLOUR_CHANNELS: readonly (readonly string[])[] = [
  ['red', 'diffuse_red', 'r', 'diffuse_r'],
  ['green', 'diffuse_green', 'g', 'diffuse_g'],
  ['blue', 'diffuse_blue', 'b', 'diffuse_b'],
];

/** Channel types written as a fraction of one rather than as a byte. */
const UNIT_SCALE_TYPES = new Set(['float', 'float32', 'double', 'float64']);

export function readPly(buffer: ArrayBuffer): PlyReading {
  const header = readHeader(buffer);
  if (!header.ok) return header.error;

  const { elements, encoding } = header.value;
  const vertex = elements.find((element) => element.name === 'vertex');

  if (!vertex || vertex.count === 0) {
    return fail(
      'no-vertices',
      'The file declares no vertices, so there is nothing to show.',
    );
  }

  return encoding === 'ascii'
    ? readAsciiBody(buffer, header.value, vertex)
    : readBinaryBody(buffer, header.value, vertex);
}

/**
 * Whether this file carries colours of its own, and on what scale.
 *
 * Read from the header alone, which is enough: the header declares every property of
 * every element, so whether there are colours and how wide they are is settled before a
 * byte of the body is touched. That is what makes this cheap enough to run on a stored
 * file that predates the field.
 *
 * A file too broken to have a header has no colours as far as this is concerned. It also
 * has nothing else, and `readPly` is what reports that.
 */
export function readPlyColours(buffer: ArrayBuffer): FileColourFormat {
  const header = readHeader(buffer);
  return header.ok ? colourFormatOf(header.value) : 'none';
}

/**
 * Does this look like PLY?
 *
 * The format opens with the word `ply` on a line of its own, which is as unambiguous a
 * magic number as any binary one.
 */
export function looksLikePly(buffer: ArrayBuffer): boolean {
  const opening = new TextDecoder('utf-8', { fatal: false }).decode(buffer.slice(0, 16));
  return /^ply\s/.test(opening);
}

interface Parsed<T> {
  readonly ok: true;
  readonly value: T;
}
interface Failed {
  readonly ok: false;
  readonly error: PlyReading & { ok: false };
}

function readHeader(buffer: ArrayBuffer): Parsed<Header> | Failed {
  const prefix = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(0, Math.min(buffer.byteLength, MAX_HEADER_BYTES)),
  );

  const terminator = /^end_header\r?\n/m.exec(prefix);
  if (!prefix.startsWith('ply') || !terminator) {
    return {
      ok: false,
      error: fail('no-header', 'This does not look like a PLY file.'),
    };
  }

  // Measured in bytes rather than characters: a header may carry a comment with an
  // accented character in it, and counting those as one byte would offset the whole body.
  const headerText = prefix.slice(0, terminator.index + terminator[0].length);
  const dataOffset = new TextEncoder().encode(headerText).length;

  let encoding: ModelEncoding | undefined;
  let littleEndian = true;
  const elements: Element[] = [];

  for (const rawLine of headerText.split('\n')) {
    const parts = rawLine.trim().split(/\s+/);
    const keyword = parts[0];

    if (keyword === 'format') {
      const written = parts[1];
      if (written === 'ascii') encoding = 'ascii';
      else if (written === 'binary_little_endian') {
        encoding = 'binary';
        littleEndian = true;
      } else if (written === 'binary_big_endian') {
        encoding = 'binary';
        littleEndian = false;
      }
      continue;
    }

    if (keyword === 'element') {
      const count = Number(parts[2]);
      if (!Number.isInteger(count) || count < 0) {
        return headerFailure('An element declares a count that is not a whole number.');
      }
      elements.push({ name: parts[1] ?? '', count, properties: [] });
      continue;
    }

    if (keyword === 'property') {
      const element = elements[elements.length - 1];
      if (!element) continue;

      if (parts[1] === 'list') {
        element.properties.push({
          name: parts[4] ?? '',
          type: parts[3] ?? '',
          countType: parts[2] ?? '',
        });
      } else {
        element.properties.push({ name: parts[2] ?? '', type: parts[1] ?? '' });
      }
    }
  }

  if (encoding === undefined) {
    return headerFailure('The file does not say how its data is written.');
  }

  return { ok: true, value: { encoding, littleEndian, elements, dataOffset } };
}

/**
 * Which element's colours would actually be drawn, and how they are scaled.
 *
 * Faces are looked at before vertices because that is the order the renderer resolves
 * them in: a file carrying both has its face colours overwrite its vertex colours, and
 * reporting the vertex scale for a file drawn from its faces would rescale the wrong
 * numbers.
 *
 * A channel written as a list is not a colour — no PLY writes one that way, and treating
 * a list property as a scalar would misreport the scale.
 */
function colourFormatOf(header: Header): FileColourFormat {
  for (const name of ['face', 'vertex']) {
    const element = header.elements.find((candidate) => candidate.name === name);
    if (!element) continue;

    const channels = COLOUR_CHANNELS.map((aliases) =>
      element.properties.find(
        (property) => property.countType === undefined && aliases.includes(property.name),
      ),
    );

    // All three or none: two channels of a colour is not a colour, and three.js draws
    // nothing from them either.
    if (channels.some((channel) => channel === undefined)) continue;

    // Any channel written as a fraction makes the whole colour one. Mixing the two within
    // a colour is not something a real file does, and reading a fractional channel as a
    // byte is what turns a model black.
    return channels.some((channel) => UNIT_SCALE_TYPES.has(channel?.type ?? ''))
      ? 'unit'
      : 'byte';
  }

  return 'none';
}

function readAsciiBody(buffer: ArrayBuffer, header: Header, vertex: Element): PlyReading {
  const body = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(header.dataOffset),
  );

  const axes = axisIndices(vertex);
  if (!axes) return fail('bad-header', 'The vertices carry no x, y and z coordinates.');

  const bounds = new BoundsAccumulator();
  const lines = body.split('\n');
  let at = 0;

  const nextRecord = (): string[] | undefined => {
    while (at < lines.length) {
      const line = lines[at]?.trim() ?? '';
      at += 1;
      if (line !== '') return line.split(/\s+/);
    }
    return undefined;
  };

  for (let index = 0; index < vertex.count; index += 1) {
    const fields = nextRecord();
    if (!fields) {
      return fail('truncated', 'The file ends before all its vertices are given.');
    }

    const included = bounds.add(
      Number(fields[axes.x]),
      Number(fields[axes.y]),
      Number(fields[axes.z]),
    );
    if (!included) {
      return fail(
        'non-finite-vertex',
        'The file contains a vertex that is not a finite number, so it cannot be rendered.',
      );
    }
  }

  // Elements are written in the order the header declares them; anything before the faces
  // has to be stepped over before the faces can be counted.
  let triangleCount = 0;
  const faces = header.elements.find((element) => element.name === 'face');

  if (faces) {
    for (const element of header.elements) {
      if (element === faces) break;
      if (element === vertex) continue;
      for (let index = 0; index < element.count; index += 1) nextRecord();
    }

    for (let index = 0; index < faces.count; index += 1) {
      const fields = nextRecord();
      if (!fields) break;
      const corners = Number(fields[0]);
      if (Number.isFinite(corners) && corners >= 3) triangleCount += corners - 2;
    }
  }

  return {
    ok: true,
    stats: {
      kind: 'ply',
      encoding: 'ascii',
      triangleCount,
      bounds: bounds.bounds(),
      fileColours: colourFormatOf(header),
    },
  };
}

function readBinaryBody(
  buffer: ArrayBuffer,
  header: Header,
  vertex: Element,
): PlyReading {
  const axes = axisOffsets(vertex);
  if (!axes) {
    return fail('bad-header', 'The vertices carry no x, y and z coordinates.');
  }

  const view = new DataView(buffer);
  const little = header.littleEndian;
  const bounds = new BoundsAccumulator();
  let at = header.dataOffset;

  for (const element of header.elements) {
    if (element === vertex) {
      const stride = axes.stride;
      const needed = stride * element.count;
      if (at + needed > buffer.byteLength) {
        return fail('truncated', 'The file ends before all its vertices are given.');
      }

      for (let index = 0; index < element.count; index += 1) {
        const base = at + index * stride;
        const included = bounds.add(
          readScalar(view, base + axes.x.offset, axes.x.type, little),
          readScalar(view, base + axes.y.offset, axes.y.type, little),
          readScalar(view, base + axes.z.offset, axes.z.type, little),
        );
        if (!included) {
          return fail(
            'non-finite-vertex',
            'The file contains a vertex that is not a finite number, so it cannot be rendered.',
          );
        }
      }

      at += needed;
      continue;
    }

    // Every other element is stepped over. Lists have to be walked one record at a time,
    // since each carries its own length; fixed-size elements can be skipped in one go.
    const walked = skipElement(view, at, element, little, buffer.byteLength);
    if (walked === undefined) {
      return fail('truncated', 'The file ends part way through its data.');
    }
    at = walked;
  }

  return {
    ok: true,
    stats: {
      kind: 'ply',
      encoding: 'binary',
      triangleCount: countBinaryFaces(view, header, buffer.byteLength),
      bounds: bounds.bounds(),
      fileColours: colourFormatOf(header),
    },
  };
}

/** Walk to the faces and add up their corners. Returns 0 if the file has no faces. */
function countBinaryFaces(view: DataView, header: Header, length: number): number {
  const faces = header.elements.find((element) => element.name === 'face');
  if (!faces) return 0;

  let at = header.dataOffset;
  for (const element of header.elements) {
    if (element === faces) break;
    const walked = skipElement(view, at, element, header.littleEndian, length);
    if (walked === undefined) return 0;
    at = walked;
  }

  const list = faces.properties.find((property) => property.countType !== undefined);
  if (!list?.countType) return 0;

  const countBytes = SCALAR_BYTES.get(list.countType) ?? 0;
  const indexBytes = SCALAR_BYTES.get(list.type) ?? 0;
  if (countBytes === 0 || indexBytes === 0) return 0;

  let triangles = 0;
  for (let index = 0; index < faces.count; index += 1) {
    if (at + countBytes > length) break;
    const corners = readScalar(view, at, list.countType, header.littleEndian);
    at += countBytes + corners * indexBytes;
    if (at > length) break;
    if (corners >= 3) triangles += corners - 2;
  }

  return triangles;
}

/** The byte after this element's records, or undefined if the file is too short. */
function skipElement(
  view: DataView,
  start: number,
  element: Element,
  little: boolean,
  length: number,
): number | undefined {
  const lists = element.properties.filter((property) => property.countType !== undefined);

  if (lists.length === 0) {
    const stride = element.properties.reduce(
      (total, property) => total + (SCALAR_BYTES.get(property.type) ?? 0),
      0,
    );
    const end = start + stride * element.count;
    return end <= length ? end : undefined;
  }

  let at = start;
  for (let index = 0; index < element.count; index += 1) {
    for (const property of element.properties) {
      if (property.countType === undefined) {
        at += SCALAR_BYTES.get(property.type) ?? 0;
        continue;
      }

      const countBytes = SCALAR_BYTES.get(property.countType) ?? 0;
      if (at + countBytes > length) return undefined;
      const items = readScalar(view, at, property.countType, little);
      at += countBytes + items * (SCALAR_BYTES.get(property.type) ?? 0);
    }
    if (at > length) return undefined;
  }

  return at;
}

function readScalar(
  view: DataView,
  offset: number,
  type: string,
  little: boolean,
): number {
  switch (type) {
    case 'char':
    case 'int8':
      return view.getInt8(offset);
    case 'uchar':
    case 'uint8':
      return view.getUint8(offset);
    case 'short':
    case 'int16':
      return view.getInt16(offset, little);
    case 'ushort':
    case 'uint16':
      return view.getUint16(offset, little);
    case 'int':
    case 'int32':
      return view.getInt32(offset, little);
    case 'uint':
    case 'uint32':
      return view.getUint32(offset, little);
    case 'double':
    case 'float64':
      return view.getFloat64(offset, little);
    default:
      return view.getFloat32(offset, little);
  }
}

/** Which fields of an ASCII vertex record hold x, y and z. */
function axisIndices(vertex: Element): { x: number; y: number; z: number } | undefined {
  const x = vertex.properties.findIndex((property) => property.name === 'x');
  const y = vertex.properties.findIndex((property) => property.name === 'y');
  const z = vertex.properties.findIndex((property) => property.name === 'z');

  return x === -1 || y === -1 || z === -1 ? undefined : { x, y, z };
}

/** Where x, y and z sit within a binary vertex record, and how long the record is. */
function axisOffsets(vertex: Element):
  | {
      x: { offset: number; type: string };
      y: { offset: number; type: string };
      z: { offset: number; type: string };
      stride: number;
    }
  | undefined {
  const found = new Map<string, { offset: number; type: string }>();
  let stride = 0;

  for (const property of vertex.properties) {
    // A list inside a vertex record has no fixed stride, so the record cannot be indexed
    // into. Nothing writes such a file; refusing beats reading it at the wrong offsets.
    if (property.countType !== undefined) return undefined;

    const size = SCALAR_BYTES.get(property.type);
    if (size === undefined) return undefined;

    if (property.name === 'x' || property.name === 'y' || property.name === 'z') {
      found.set(property.name, { offset: stride, type: property.type });
    }
    stride += size;
  }

  const x = found.get('x');
  const y = found.get('y');
  const z = found.get('z');
  return x && y && z ? { x, y, z, stride } : undefined;
}

function fail(problem: PlyProblem, message: string): PlyReading & { ok: false } {
  return { ok: false, problem, message };
}

function headerFailure(message: string): Failed {
  return { ok: false, error: fail('bad-header', message) };
}

/**
 * Names an alpha channel may go by, alongside the three in `COLOUR_CHANNELS`.
 *
 * Unlike the colour channels this list is ours rather than three's, because three's loader
 * has no alpha handling at all: it reads red, green and blue and stops. A PLY that says
 * how see-through it is has been saying so to nobody.
 */
const ALPHA_CHANNELS = ['alpha', 'diffuse_alpha', 'a', 'diffuse_a'];

/**
 * Per-vertex opacity, from nought to one, in the order the vertices appear.
 *
 * `undefined` where the file declares no alpha channel, which is the ordinary case and
 * means the model is opaque — distinct from a file that declares one and fills it with
 * ones, which is opaque by its own account and costs an array to say so.
 *
 * Read in a pass of its own rather than alongside the bounds. It is wanted only when a
 * model is built, which may be days after it was measured or never, and an array of one
 * float per vertex is not a thing to keep against that.
 */
export function readPlyAlpha(buffer: ArrayBuffer): Float32Array | undefined {
  const header = readHeader(buffer);
  if (!header.ok) return undefined;

  const vertex = header.value.elements.find((element) => element.name === 'vertex');
  if (!vertex || vertex.count === 0) return undefined;

  const at = vertex.properties.findIndex(
    (property) =>
      property.countType === undefined && ALPHA_CHANNELS.includes(property.name),
  );
  if (at === -1) return undefined;

  const type = vertex.properties[at]?.type ?? '';
  // The same scale question the colours themselves pose: a channel written as a byte runs
  // to 255 and one written as a fraction runs to one, and the values alone cannot say
  // which — 1 is a legitimate byte, and very nearly transparent.
  const full = UNIT_SCALE_TYPES.has(type) ? 1 : 255;

  const values =
    header.value.encoding === 'ascii'
      ? readAsciiAlpha(buffer, header.value, vertex, at)
      : readBinaryAlpha(buffer, header.value, vertex, at);

  if (!values) return undefined;
  for (let index = 0; index < values.length; index += 1) {
    values[index] = Math.min(1, Math.max(0, (values[index] ?? full) / full));
  }

  return values;
}

function readAsciiAlpha(
  buffer: ArrayBuffer,
  header: Header,
  vertex: Element,
  at: number,
): Float32Array | undefined {
  const body = new TextDecoder('utf-8', { fatal: false }).decode(
    buffer.slice(header.dataOffset),
  );

  const values = new Float32Array(vertex.count);
  const lines = body.split('\n');
  let line = 0;

  for (let index = 0; index < vertex.count; index += 1) {
    let fields: string[] | undefined;
    while (line < lines.length && !fields) {
      const text = lines[line]?.trim() ?? '';
      line += 1;
      if (text !== '') fields = text.split(/\s+/);
    }
    if (!fields) return undefined;

    const value = Number(fields[at]);
    values[index] = Number.isFinite(value) ? value : Number.NaN;
  }

  return values;
}

function readBinaryAlpha(
  buffer: ArrayBuffer,
  header: Header,
  vertex: Element,
  at: number,
): Float32Array | undefined {
  const layout = recordLayout(vertex);
  if (!layout) return undefined;

  const field = layout.fields[at];
  if (!field) return undefined;

  const view = new DataView(buffer);
  const values = new Float32Array(vertex.count);
  let start = header.dataOffset;

  // Everything declared before the vertices has to be stepped over first; nothing usually
  // is, but the format allows it and reading at the wrong offset produces noise.
  for (const element of header.elements) {
    if (element === vertex) break;
    const walked = skipElement(
      view,
      start,
      element,
      header.littleEndian,
      buffer.byteLength,
    );
    if (walked === undefined) return undefined;
    start = walked;
  }

  if (start + layout.stride * vertex.count > buffer.byteLength) return undefined;

  for (let index = 0; index < vertex.count; index += 1) {
    values[index] = readScalar(
      view,
      start + index * layout.stride + field.offset,
      field.type,
      header.littleEndian,
    );
  }

  return values;
}

/** Where each property of a fixed-size vertex record sits, and how long the record is. */
function recordLayout(
  vertex: Element,
): { fields: { offset: number; type: string }[]; stride: number } | undefined {
  const fields: { offset: number; type: string }[] = [];
  let stride = 0;

  for (const property of vertex.properties) {
    // A list inside a vertex record has no fixed stride, so the record cannot be indexed
    // into. Nothing writes such a file; refusing beats reading it at the wrong offsets.
    if (property.countType !== undefined) return undefined;

    const size = SCALAR_BYTES.get(property.type);
    if (size === undefined) return undefined;

    fields.push({ offset: stride, type: property.type });
    stride += size;
  }

  return { fields, stride };
}
